import React, { useState, useEffect, useRef } from "react";
import { db } from "../../lib/firebase";
import { collection, getDocs, getDoc, doc, updateDoc, query, where, orderBy, limit, startAt, endAt, startAfter, getCountFromServer } from "firebase/firestore";
import { useNotification } from "../../common/NotificationContext";
import {
  LuPackage,
  LuSearch,
  LuLoader,
  LuCheck,
  LuUndo,
  LuBox,
  LuUsers,
  LuActivity,
  LuX,
  LuBarcode,
  LuFilter
} from "react-icons/lu";
import { InputField, Button } from "@cakrawalajakarta/shared-components";

// High code-point sentinel for Firestore prefix range queries: [q, q+SENTINEL].
const SEARCH_SENTINEL = String.fromCharCode(0xf8ff);
const SUGGEST_PAGE_SIZE = 20; // per source, per "load more" page
// Normalized name for ordered prefix search — MUST stay in sync with the server
// (payment.js normalizeForSearch) and the backfill script.
const normalizeForSearch = (v) =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const Racepack = () => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestHasMore, setSuggestHasMore] = useState(false);
  const [suggestLoadingMore, setSuggestLoadingMore] = useState(false);
  const suggestCursors = useRef({});   // per-source last doc snapshot
  const suggestDone = useRef(new Set()); // sources exhausted for current prefix
  const suggestToken = useRef("");     // normalized query the cursors belong to
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [delegateName, setDelegateName] = useState("");
  const [showDelegateSuggestions, setShowDelegateSuggestions] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [lastVisible, setLastVisible] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);
  const [stats, setStats] = useState({ total: 0, collected: 0, pending: 0, rpc1: 0, rpc1Collected: 0, rpc1Pending: 0, rpc2: 0, rpc2Collected: 0, rpc2Pending: 0 });
  const { showNotification, showConfirm } = useNotification();
  const searchInputRef = useRef(null);
  const topRef = useRef(null);
  const isFirstRender = useRef(true);

  const RACEPACK_EVENT_SOURCES = [
    { eventId: "JOTR2026", collectionName: "pendaftaranJOTR2026" },
    { eventId: "ROADTOJOTR2026", collectionName: "pendaftaranRoadToJOTR2026" },
    { eventId: "FUNRUN5K2026", collectionName: "pendaftaranFunRun5K2026" },
  ];
  const PAGE_SIZE = 50;

  const getParticipantKey = (participant) =>
    `${participant.collectionName || participant.eventId || "event"}-${participant.id}`;

  const getParticipantCategoryLabel = (participant) =>
    participant.kategori || participant.category || "-";

  const getParticipantCollectionName = (participant) =>
    participant.collectionName ||
    RACEPACK_EVENT_SOURCES.find((source) => source.eventId === participant.eventId)?.collectionName ||
    "pendaftaranJOTR2026";

  useEffect(() => {
    fetchPage("init");
    fetchStats();
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [statusFilter, locationFilter]);

  useEffect(() => {
    setDelegateName("");
    setShowDelegateSuggestions(false);
  }, [selectedParticipant]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchPage("init");
    }, 600);
    return () => clearTimeout(timer);
  }, [listSearch]);

  const fetchStats = async () => {
    try {
      const counts = await Promise.all(
        RACEPACK_EVENT_SOURCES.map(async ({ collectionName }) => {
          const coll = collection(db, collectionName);
          const [
            settlementSnap, captureSnap,
            collectedSettlementSnap, collectedCaptureSnap,
            rpc1Settlement, rpc1Capture,
            rpc2Settlement, rpc2Capture,
            rpc1CollectedSettlement, rpc1CollectedCapture,
            rpc2CollectedSettlement, rpc2CollectedCapture,
            transferredOutDocs,
          ] = await Promise.all([
            getCountFromServer(query(coll, where("transaction_status", "==", "settlement"))),
            getCountFromServer(query(coll, where("transaction_status", "==", "capture"))),
            getCountFromServer(query(coll, where("transaction_status", "==", "settlement"), where("racepackCollected", "==", true))),
            getCountFromServer(query(coll, where("transaction_status", "==", "capture"), where("racepackCollected", "==", true))),
            getCountFromServer(query(coll, where("transaction_status", "==", "settlement"), where("rpcLocation", "==", "Jakarta Decathlon Semanggi"))),
            getCountFromServer(query(coll, where("transaction_status", "==", "capture"), where("rpcLocation", "==", "Jakarta Decathlon Semanggi"))),
            getCountFromServer(query(coll, where("transaction_status", "==", "settlement"), where("rpcLocation", "==", "TNGP Cibodas"))),
            getCountFromServer(query(coll, where("transaction_status", "==", "capture"), where("rpcLocation", "==", "TNGP Cibodas"))),
            getCountFromServer(query(coll, where("transaction_status", "==", "settlement"), where("rpcLocation", "==", "Jakarta Decathlon Semanggi"), where("racepackCollected", "==", true))),
            getCountFromServer(query(coll, where("transaction_status", "==", "capture"), where("rpcLocation", "==", "Jakarta Decathlon Semanggi"), where("racepackCollected", "==", true))),
            getCountFromServer(query(coll, where("transaction_status", "==", "settlement"), where("rpcLocation", "==", "TNGP Cibodas"), where("racepackCollected", "==", true))),
            getCountFromServer(query(coll, where("transaction_status", "==", "capture"), where("rpcLocation", "==", "TNGP Cibodas"), where("racepackCollected", "==", true))),
            // Senders whose slot was transferred out: still settlement, but they
            // will NOT collect a racepack — the receiver does. Exclude them so the
            // logistics counts reflect real racepack recipients (reconciles with
            // the 1,487 verified figure). Fetch the docs (small set) to also
            // subtract per RPC location without needing a composite index.
            getDocs(query(coll, where("bibTransfer.transferredOut", "==", true))),
          ]);
          let outTotal = 0, outRpc1 = 0, outRpc2 = 0;
          transferredOutDocs.forEach((d) => {
            outTotal += 1;
            const loc = d.data().rpcLocation;
            if (loc === "Jakarta Decathlon Semanggi") outRpc1 += 1;
            else if (loc === "TNGP Cibodas") outRpc2 += 1;
          });
          return {
            total: settlementSnap.data().count + captureSnap.data().count - outTotal,
            collected: collectedSettlementSnap.data().count + collectedCaptureSnap.data().count,
            rpc1: rpc1Settlement.data().count + rpc1Capture.data().count - outRpc1,
            rpc1Collected: rpc1CollectedSettlement.data().count + rpc1CollectedCapture.data().count,
            rpc2: rpc2Settlement.data().count + rpc2Capture.data().count - outRpc2,
            rpc2Collected: rpc2CollectedSettlement.data().count + rpc2CollectedCapture.data().count,
          };
        })
      );
      const total = counts.reduce((sum, item) => sum + item.total, 0);
      const collected = counts.reduce((sum, item) => sum + item.collected, 0);
      const rpc1 = counts.reduce((sum, item) => sum + item.rpc1, 0);
      const rpc1Collected = counts.reduce((sum, item) => sum + item.rpc1Collected, 0);
      const rpc2 = counts.reduce((sum, item) => sum + item.rpc2, 0);
      const rpc2Collected = counts.reduce((sum, item) => sum + item.rpc2Collected, 0);
      
      setStats({ 
        total, 
        collected, 
        pending: total - collected, 
        rpc1, 
        rpc1Collected,
        rpc1Pending: rpc1 - rpc1Collected,
        rpc2,
        rpc2Collected,
        rpc2Pending: rpc2 - rpc2Collected
      });
    } catch (e) {
      console.error("Stats error:", e);
    }
  };

  const enrichWithAvatars = async (list) => {
    const uids = [...new Set(list.map(p => p.uid).filter(Boolean))];
    if (!uids.length) return list;
    const photoMap = {};
    await Promise.all(
      uids.map(async uid => {
        try {
          const snap = await getDoc(doc(db, "users", uid));
          if (snap.exists()) photoMap[uid] = snap.data().photoURL || snap.data().photoUrl || null;
        } catch {}
      })
    );
    return list.map(p => ({ ...p, photoURL: photoMap[p.uid] || p.photoURL || p.photoUrl || null }));
  };

  const fetchPage = async (action = "init") => {
    action === "init" ? setLoading(true) : setLoadingMore(true);
    try {
      const buildQueriesForSource = (source) => {
        const baseConstraints = [];
        if (statusFilter === "collected") baseConstraints.push(where("racepackCollected", "==", true));
        if (locationFilter !== "all") {
          baseConstraints.push(where("rpcLocation", "==", locationFilter));
        }

        if (listSearch) {
          baseConstraints.push(where("searchKeywords", "array-contains", listSearch.trim().toLowerCase()));
        } else {
          baseConstraints.push(orderBy("namaLengkap"));
        }

        if (action === "more" && lastVisible?.[source.collectionName]) {
          baseConstraints.push(startAfter(lastVisible[source.collectionName]));
        }
        baseConstraints.push(limit(PAGE_SIZE));

        return ["settlement", "capture"].map((paymentStatus) =>
          query(
            collection(db, source.collectionName),
            where("transaction_status", "==", paymentStatus),
            ...baseConstraints
          )
        );
      };

      const snapshots = await Promise.all(
        RACEPACK_EVENT_SOURCES.flatMap((source) =>
          buildQueriesForSource(source).map(async (sourceQuery) => ({
            source,
            snapshot: await getDocs(sourceQuery),
          }))
        )
      );

      const cursorMap = {};
      snapshots.forEach(({ source, snapshot }) => {
        if (snapshot.docs.length > 0) {
          cursorMap[source.collectionName] = snapshot.docs[snapshot.docs.length - 1];
        }
      });

      let list = snapshots.flatMap(({ source, snapshot }) =>
        snapshot.docs.map((d) => ({
          id: d.id,
          collectionName: source.collectionName,
          eventId: source.eventId,
          ...d.data(),
        }))
      );
      list = await enrichWithAvatars(list);
      
      if (statusFilter === "awaiting") {
        list = list.filter((p) => !p.racepackCollected);
      }
      
      list.sort((a,b) => (a.namaLengkap || a.teamName || "").localeCompare(b.namaLengkap || b.teamName || ""));
      list = list.slice(0, PAGE_SIZE);

      if (Object.keys(cursorMap).length > 0) {
        setLastVisible(action === "more" ? { ...(lastVisible || {}), ...cursorMap } : cursorMap);
      }
      setIsLastPage(list.length < PAGE_SIZE);

      if (action === "more") setParticipants(prev => [...prev, ...list]);
      else setParticipants(list);
    } catch (error) {
      console.error("Error fetching:", error);
      showNotification("error", "Failed to load participants");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch one page of name-prefix results for the Scan box. Uses an ordered range
  // query on `namaLengkapLower` (index-backed, no arbitrary cap) so results are
  // globally sorted and every match is reachable via cursor pagination.
  const fetchSuggestionPage = async (qNorm, isMore) => {
    const snaps = await Promise.all(
      RACEPACK_EVENT_SOURCES.map(async (source) => {
        if (isMore && suggestDone.current.has(source.collectionName)) {
          return { source, docs: [] };
        }
        const cursor = suggestCursors.current[source.collectionName];
        const rangeStart = isMore && cursor ? startAfter(cursor) : startAt(qNorm);
        const q = query(
          collection(db, source.collectionName),
          orderBy("namaLengkapLower"),
          rangeStart,
          endAt(qNorm + SEARCH_SENTINEL),
          limit(SUGGEST_PAGE_SIZE)
        );
        const snap = await getDocs(q);
        if (snap.docs.length > 0) {
          suggestCursors.current[source.collectionName] = snap.docs[snap.docs.length - 1];
        }
        if (snap.docs.length < SUGGEST_PAGE_SIZE) suggestDone.current.add(source.collectionName);
        return {
          source,
          docs: snap.docs.map((d) => ({ id: d.id, collectionName: source.collectionName, eventId: source.eventId, ...d.data() })),
        };
      })
    );
    // Only paid participants show up in racepack (filter client-side to keep the
    // query a single-field index; unpaid docs in the prefix are simply hidden).
    const page = snaps
      .flatMap((s) => s.docs)
      .filter((p) => p.transaction_status === "settlement" || p.transaction_status === "capture");
    const hasMore = RACEPACK_EVENT_SOURCES.some((s) => !suggestDone.current.has(s.collectionName));
    return { page, hasMore };
  };

  // Order ID, BIB, identity number, and other registration identifiers live in
  // `searchKeywords`. Fetch these alongside the ordered name-prefix results so
  // the Scan E-Ticket dropdown supports every identifier accepted by Enter.
  const fetchKeywordSuggestions = async (qNorm) => {
    const snaps = await Promise.all(
      RACEPACK_EVENT_SOURCES.flatMap((source) =>
        ["settlement", "capture"].map(async (paymentStatus) => ({
          source,
          snapshot: await getDocs(
            query(
              collection(db, source.collectionName),
              where("transaction_status", "==", paymentStatus),
              where("searchKeywords", "array-contains", qNorm),
              limit(SUGGEST_PAGE_SIZE)
            )
          ),
        }))
      )
    );

    return snaps.flatMap(({ source, snapshot }) =>
      snapshot.docs.map((d) => ({
        id: d.id,
        collectionName: source.collectionName,
        eventId: source.eventId,
        ...d.data(),
      }))
    );
  };

  const mergeSortSuggestions = (list) => {
    const seen = new Set();
    const out = [];
    for (const p of list) {
      const k = getParticipantKey(p);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(p);
    }
    out.sort((a, b) => (a.namaLengkapLower || "").localeCompare(b.namaLengkapLower || ""));
    return out;
  };

  // Debounced first page whenever the query changes.
  useEffect(() => {
    const qNorm = normalizeForSearch(searchQuery);
    if (qNorm.length < 2) {
      setSuggestions([]);
      setSuggestHasMore(false);
      setSuggestLoading(false);
      return;
    }
    let cancelled = false;
    setSuggestLoading(true);
    const timer = setTimeout(async () => {
      // reset pagination state for the new query
      suggestCursors.current = {};
      suggestDone.current = new Set();
      suggestToken.current = qNorm;
      try {
        const [{ page, hasMore }, keywordMatches] = await Promise.all([
          fetchSuggestionPage(qNorm, false),
          fetchKeywordSuggestions(qNorm),
        ]);
        if (cancelled) return;
        setSuggestions(mergeSortSuggestions([...keywordMatches, ...page]));
        setSuggestHasMore(hasMore);
      } catch (e) {
        console.error("Suggestion lookup failed", e);
        if (!cancelled) { setSuggestions([]); setSuggestHasMore(false); }
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchQuery]);

  const loadMoreSuggestions = async () => {
    if (suggestLoadingMore) return;
    setSuggestLoadingMore(true);
    try {
      const { page, hasMore } = await fetchSuggestionPage(suggestToken.current, true);
      setSuggestions((prev) => mergeSortSuggestions([...prev, ...page]));
      setSuggestHasMore(hasMore);
    } catch (e) {
      console.error("Load more suggestions failed", e);
    } finally {
      setSuggestLoadingMore(false);
    }
  };

  const handleScan = async () => {
    if (!searchQuery.trim()) return;
    const qTerm = searchQuery.trim().toLowerCase();
    try {
      // Use searchKeywords to robustly search Order ID, Identity Number, BIB, or Name
      const snapshots = await Promise.all(
        RACEPACK_EVENT_SOURCES.flatMap((source) =>
          ["settlement", "capture"].map(async (paymentStatus) => ({
            source,
            snapshot: await getDocs(query(
              collection(db, source.collectionName),
              where("transaction_status", "==", paymentStatus),
              where("searchKeywords", "array-contains", qTerm),
              limit(1)
            )),
          }))
        )
      );
      const foundResult = snapshots.find(({ snapshot }) => !snapshot.empty);
      const found = foundResult?.snapshot.docs[0];
      if (found) {
        setSelectedParticipant({
          id: found.id,
          collectionName: foundResult.source.collectionName,
          eventId: foundResult.source.eventId,
          ...found.data(),
        });
        setSearchQuery("");
        if (searchInputRef.current) searchInputRef.current.focus();
      } else {
        showNotification("info", "No paid participant found matching that ID");
      }
    } catch (e) {
      console.error(e);
      showNotification("error", "Scan lookup failed");
    }
  };

  const handleMarkCollected = async (participant) => {
    setProcessing(true);
    try {
      const timestamp = new Date().toLocaleString('en-US');
      await updateDoc(doc(db, getParticipantCollectionName(participant), participant.id), {
        racepackCollected: true,
        racepackCollectedAt: timestamp,
        collectedBy: delegateName.trim() || null,
      });
      
      setParticipants(prev => prev.map(p => 
        getParticipantKey(p) === getParticipantKey(participant) ? { ...p, racepackCollected: true, racepackCollectedAt: timestamp, collectedBy: delegateName.trim() || null } : p
      ));
      
      setSelectedParticipant(prev => ({ ...prev, racepackCollected: true, racepackCollectedAt: timestamp, collectedBy: delegateName.trim() || null }));
      fetchStats(); // Update the top stats
      // showNotification("success", `Racepack marked as collected for ${participant.namaLengkap}`);
    } catch (error) {
      console.error("Error marking collected:", error);
      showNotification("error", "Failed to update record");
    } finally {
      setProcessing(false);
    }
  };

  const handleUndoCollection = (participant) => {
    showConfirm(
      "Undo this collection? Data will be reset.",
      () => _doUndoCollection(participant),
      "Undo",
      "Cancel"
    );
  };

  const _doUndoCollection = async (participant) => {
    setProcessing(true);
    try {
      await updateDoc(doc(db, getParticipantCollectionName(participant), participant.id), {
        racepackCollected: false,
        racepackCollectedAt: null,
        collectedBy: null,
      });
      
      setParticipants(prev => prev.map(p => 
        getParticipantKey(p) === getParticipantKey(participant) ? { ...p, racepackCollected: false, racepackCollectedAt: null, collectedBy: null } : p
      ));
      
      setSelectedParticipant(prev => ({ ...prev, racepackCollected: false, racepackCollectedAt: null, collectedBy: null }));
      fetchStats(); // Update the top stats
      // showNotification("info", "Collection undone");
    } catch (error) {
      console.error("Error undoing:", error);
      showNotification("error", "Failed to undo record");
    } finally {
      setProcessing(false);
    }
  };

  const filteredList = participants;

  return (
    <>
      <div ref={topRef} />
      <div className="space-y-4 pb-20">
        {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuPackage size={120} />
         </div>
         <div className="relative z-10 flex flex-col md:flex-row justify-between items-start gap-4">
            <div>
               <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                  <LuActivity size={14} /> RPC Management
               </p>
               <h1 className="text-2xl md:text-3xl font-bold mb-1">Racepack Collection</h1>
               <p className="text-slate-500 text-sm max-w-xl">
                  Search participants and mark their racepack as collected.
               </p>
            </div>
            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-lg text-xs font-semibold border border-green-100">
               <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
               USB SCANNER READY
            </div>
         </div>
      </div>

      {/* Split Pane: Scan and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-col-reverse">
        
        {/* Left Card: Scanner */}
        <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200 p-6 flex flex-col items-center justify-center min-h-[250px]">
           <div className="mb-6">
              <LuBarcode size={60} className="text-slate-300" />
           </div>
           <h2 className="text-xl font-bold text-slate-800 mb-2 text-center">Scan E-Ticket</h2>
           <p className="text-slate-400 text-center text-sm mb-6">Focus input and scan QR Code or type ID</p>
           
           <div className="w-full max-w-sm relative">
             <InputField 
                ref={searchInputRef}
                name="scan"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleScan()}
                placeholder="Scan QR or type name/ID..."
                startIcon={<LuSearch className="text-slate-400" />}
                labelClassName="sr-only"
                autoComplete="off"
             />
             
             {/* Suggestions Dropdown (server-backed) */}
             {searchQuery.length >= 2 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-[300px] overflow-y-auto custom-scrollbar">
                   {suggestions.map((p) => (
                      <div
                         key={getParticipantKey(p)}
                         onClick={() => {
                            setSelectedParticipant(p);
                            setSearchQuery("");
                         }}
                         className="p-3 border-b border-slate-50 last:border-none hover:bg-slate-50 cursor-pointer flex justify-between items-center transition-colors"
                      >
                         <div>
                            <p className="font-bold text-slate-800 text-sm leading-tight">{p.namaLengkap}</p>
                            <p className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">{getParticipantCategoryLabel(p)}</p>
                            {p.orderId && (
                               <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.orderId}</p>
                            )}
                         </div>
                         <div className="text-right">
                            <p className="font-bold text-slate-800 text-sm tracking-tight">{p.bibNumber || "-"}</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase">{p.sizeJersey || "-"}</p>
                         </div>
                      </div>
                   ))}
                   {suggestLoading && suggestions.length === 0 && (
                      <div className="p-4 flex items-center justify-center gap-2 text-xs text-slate-400 italic"><LuLoader className="animate-spin" /> Searching…</div>
                   )}
                   {!suggestLoading && suggestions.length === 0 && (
                      <div className="p-4 text-center text-xs text-slate-400 italic">No matching participants</div>
                   )}
                   {suggestHasMore && suggestions.length > 0 && (
                      <button
                         type="button"
                         onClick={loadMoreSuggestions}
                         disabled={suggestLoadingMore}
                         className="w-full p-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-50 border-t border-slate-100 flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                         {suggestLoadingMore ? <><LuLoader className="animate-spin" /> Loading…</> : "Load more"}
                      </button>
                   )}
                </div>
             )}
             
             <p className="text-[10px] text-slate-300 mt-2 text-center uppercase tracking-widest font-medium">Auto-detects REG-ID, URL, or Name</p>
           </div>
        </div>

        {/* Right Card: Details */}
        <div className="lg:col-span-7 bg-white rounded-xl border border-slate-200 min-h-[250px] relative">
           {!selectedParticipant ? (
               <div className="h-full flex flex-col items-center justify-center p-12 text-center opacity-40">
                  <LuBox size={60} className="text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-500">No Participant Selected</h3>
                  <p className="text-slate-400 text-sm">Scan or search to view details</p>
               </div>
           ) : (
               <div className="h-full flex flex-col">
                  {/* Status Indicator */}
                  {selectedParticipant.racepackCollected ? (
                      <div className="bg-green-50 border-b border-green-100 p-3 flex flex-col gap-2 text-green-700 rounded-t-xl">
                         <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                               <LuPackage className="text-green-500" />
                               <p className="font-bold text-sm uppercase">Already Collected</p>
                            </div>
                            <p className="text-[10px] opacity-70 font-medium">{selectedParticipant.racepackCollectedAt}</p>
                         </div>
                      </div>
                  ) : (
                      <div className="bg-slate-50 border-b border-slate-100 p-3 flex items-center gap-3 text-slate-500 rounded-t-xl">
                         <LuPackage />
                         <p className="font-bold text-sm uppercase tracking-wide">Ready for Pickup</p>
                      </div>
                  )}

                  {/* Content */}
                  <div className="p-4 flex-1">
                     <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                        <div className="col-span-2 md:col-span-1 border-r border-slate-100 pr-4">
                           <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">Participant</p>
                           <h3 className="text-lg font-bold text-slate-800 leading-tight mb-0.5">{selectedParticipant.namaLengkap}</h3>
                           <p className="text-slate-500 text-xs">{selectedParticipant.nomorIdentitas || "-"}</p>
                           
                           <div className="mt-4 space-y-3">
                              <div>
                                 <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Category</p>
                                 <p className="text-sm font-medium text-slate-700">{getParticipantCategoryLabel(selectedParticipant)}</p>
                              </div>
                              <div>
                                 <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Gender</p>
                                 <p className="text-sm font-medium text-slate-700 uppercase">{selectedParticipant.gender || "-"}</p>
                              </div>
                           </div>
                        </div>

                        <div className="col-span-2 md:col-span-1 flex flex-col justify-between space-y-4">
                           <div>
                              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">Jersey Size</p>
                              <div className="text-3xl font-bold text-slate-800">
                                 {selectedParticipant.sizeJersey || "?"}
                              </div>
                           </div>
                           
                           <div>
                              <p className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-0.5">BIB Number</p>
                              <p className="text-3xl font-bold text-slate-800">{selectedParticipant.bibNumber || "PENDING"}</p>
                           </div>

                           <div>
                              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">RPC Location</p>
                              <p className="text-sm font-medium text-slate-700 uppercase">{selectedParticipant.rpcLocation || "NOT SELECTED"}</p>
                           </div>
                           {selectedParticipant.vehiclePlateNumber && (
                              <div>
                                 <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-0.5">Vehicle</p>
                                 <p className="text-sm font-medium text-slate-700 uppercase">{selectedParticipant.vehiclePlateNumber}</p>
                              </div>
                           )}
                        </div>
                     </div>

                     <div className="mt-2">
                        {/* Delegate Display or Input Field (Full Width) */}
                        {selectedParticipant.racepackCollected && selectedParticipant.collectedBy ? (
                           <div className="mb-4">
                              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-1">Collected By</p>
                              <p className="text-sm font-medium text-slate-700 uppercase">{selectedParticipant.collectedBy}</p>
                           </div>
                        ) : !selectedParticipant.racepackCollected ? (
                           <div className="mb-4 relative">
                              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-2">Collected By (Optional)</p>
                              <InputField 
                                  name="delegateName"
                                  value={delegateName}
                                  onChange={(e) => {
                                     setDelegateName(e.target.value);
                                     setShowDelegateSuggestions(true);
                                  }}
                                  placeholder="Name of delegate (optional)..."
                                  labelClassName="sr-only"
                                  autoComplete="off"
                              />
                              {/* Suggestions Dropdown */}
                              {showDelegateSuggestions && delegateName.length >= 2 && participants.filter(p => p.namaLengkap?.toLowerCase().includes(delegateName.toLowerCase()) && p.id !== selectedParticipant.id).length > 0 && (
                                 <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-[150px] overflow-y-auto custom-scrollbar">
                                    {participants
                                       .filter(p => p.namaLengkap?.toLowerCase().includes(delegateName.toLowerCase()) && p.id !== selectedParticipant.id)
                                       .slice(0, 3)
                                       .map((p) => (
                                          <div 
                                             key={getParticipantKey(p)}
                                             onClick={() => {
                                                setDelegateName(p.namaLengkap);
                                                setShowDelegateSuggestions(false);
                                             }}
                                             className="p-3 border-b border-slate-50 last:border-none hover:bg-slate-50 cursor-pointer transition-colors"
                                          >
                                             <p className="font-bold text-slate-800 text-sm leading-tight">{p.namaLengkap}</p>
                                             <p className="text-[10px] text-slate-400 font-medium uppercase mt-0.5">{getParticipantCategoryLabel(p)}</p>
                                          </div>
                                       ))}
                                 </div>
                              )}
                           </div>
                        ) : null}

                        {/* Actions */}
                        <div className="flex gap-4">
                           <Button variant="secondary" onClick={() => setSelectedParticipant(null)}>
                              Cancel
                           </Button>
                           
                           {selectedParticipant.racepackCollected ? (
                              <Button variant="danger-outline" onClick={() => handleUndoCollection(selectedParticipant)} disabled={processing} className="flex-1 uppercase tracking-widest">
                                 Undo
                              </Button>
                           ) : (
                              <Button variant="none" onClick={() => handleMarkCollected(selectedParticipant)} disabled={processing || selectedParticipant.bibTransfer?.transferredOut} className="flex-1 bg-slate-800 hover:bg-slate-900 text-white uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed">
                                 {processing ? <><LuLoader className="animate-spin text-xs" /> Processing</> : selectedParticipant.bibTransfer?.transferredOut ? "Transferred — Cannot Collect" : "Confirm"}
                              </Button>
                           )}
                        </div>
                     </div>
                  </div>
               </div>
           )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 w-full">
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuUsers size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1">Total Paid</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.total}</h3>
               <p className="text-sm text-slate-400 mt-1">Paid participants</p>
            </div>
         </div>
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuCheck size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1">Collected</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.collected}</h3>
               <p className="text-sm text-slate-400 mt-1">Packets picked up</p>
            </div>
         </div>
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuBox size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1">Pending</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">{stats.pending}</h3>
               <p className="text-sm text-slate-400 mt-1">Awaiting collection</p>
            </div>
         </div>
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuBox size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1 truncate" title="Jakarta Decathlon Semanggi">RPC Semanggi</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1 leading-none">{stats.rpc1Pending || 0}</h3>
               <p className="text-xs font-semibold text-slate-500 mt-1">
                  <span className="text-green-600">{stats.rpc1Collected || 0} Collected</span> <span className="text-slate-300 font-normal mx-1">/</span> Total: {stats.rpc1 || 0}
               </p>
            </div>
         </div>
         <div className="bg-white p-4 rounded-xl border border-slate-200 relative">
            <div className="absolute top-4 right-4 text-slate-400">
               <LuBox size={20} />
            </div>
            <div>
               <p className="text-slate-500 text-sm font-medium mb-1 truncate" title="TNGP Cibodas">RPC Cibodas</p>
               <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1 leading-none">{stats.rpc2Pending || 0}</h3>
               <p className="text-xs font-semibold text-slate-500 mt-1">
                  <span className="text-green-600">{stats.rpc2Collected || 0} Collected</span> <span className="text-slate-300 font-normal mx-1">/</span> Total: {stats.rpc2 || 0}
               </p>
            </div>
         </div>
      </div>

      {/* Filters Area */}
      <div className="bg-white rounded-xl p-4 border">
        <div className="flex flex-col md:flex-row gap-4">
           {/* Search */}
           <div className="flex-1">
              <InputField 
                 name="listSearch"
                 value={listSearch}
                 onChange={(e) => setListSearch(e.target.value)}
                 placeholder="Search Name, Email, or BIB..."
                 startIcon={<LuSearch className="text-slate-400" />}
                 labelClassName="sr-only"
              />
           </div>

           {/* Status Filter */}
           <div className="min-w-[200px]">
              <InputField 
                 type="select"
                 name="statusFilter"
                 value={statusFilter}
                 onChange={(e) => setStatusFilter(e.target.value)}
                 options={[
                    { value: "all", label: "All Status" },
                    { value: "collected", label: "Collected" },
                    { value: "awaiting", label: "Awaiting" }
                 ]}
                 startIcon={<LuFilter className="text-slate-400" />}
                 defaultOptionLabel="Filter Status"
                 labelClassName="sr-only"
              />
           </div>

           {/* Location Filter */}
           <div className="min-w-[200px]">
              <InputField 
                 type="select"
                 name="locationFilter"
                 value={locationFilter}
                 onChange={(e) => setLocationFilter(e.target.value)}
                 options={[
                    { value: "all", label: "All Locations" },
                    { value: "Jakarta Decathlon Semanggi", label: "Semanggi" },
                    { value: "TNGP Cibodas", label: "Cibodas" }
                 ]}
                 startIcon={<LuFilter className="text-slate-400" />}
                 defaultOptionLabel="Filter Location"
                 labelClassName="sr-only"
              />
           </div>
        </div>
      </div>

      {/* Participant Card List */}
      <div className="flex flex-col gap-2">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LuLoader className="animate-spin text-slate-400" size={32} />
          </div>
        ) : filteredList.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-12 text-center text-slate-400 italic text-sm">
            No paid participants found.
          </div>
        ) : filteredList.map((p) => (
          <div
            key={getParticipantKey(p)}
            className="bg-white rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => { setSelectedParticipant(p); topRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            <div className="grid grid-cols-12 gap-4 items-center">
              {/* Avatar + Name */}
              <div className="col-span-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-lime-100 flex items-center justify-center text-slate-700 font-bold flex-shrink-0 overflow-hidden">
                  {(p.photoURL || p.photoUrl) ? (
                    <img src={p.photoURL || p.photoUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  ) : null}
                  <span style={{ display: (p.photoURL || p.photoUrl) ? 'none' : 'flex' }} className="w-full h-full items-center justify-center">
                    {(p.namaLengkap || p.teamName || "?").charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{p.namaLengkap || p.teamName || "-"}</p>
                  <p className="text-[10px] text-slate-400 font-mono truncate">{p.orderId}</p>
                </div>
              </div>

              {/* Category */}
              <div className="col-span-3">
                <p className="text-sm font-medium text-slate-700 truncate">{getParticipantCategoryLabel(p)}</p>
                {p.rpcLocation && (
                  <p className="text-[10px] text-slate-500 font-bold uppercase truncate mt-0.5">{p.rpcLocation}</p>
                )}
              </div>

              {/* BIB / Jersey */}
              <div className="col-span-2 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase mb-0.5">BIB / Jersey</p>
                <p className="text-sm font-semibold text-slate-800">{p.bibNumber || "PENDING"} / {p.sizeJersey || "-"}</p>
              </div>

              {/* Status */}
              <div className="col-span-2 flex justify-center">
                {p.bibTransfer?.transferredOut ? (
                  <span className="inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                    {p.bibTransfer?.transferredToName
                      ? `BIB to ${p.bibTransfer.transferredToName.trim().split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ")}`
                      : "BIB Transferred"}
                  </span>
                ) : p.racepackCollected ? (
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                    <LuCheck size={10} /> Collected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase">
                    Awaiting
                  </span>
                )}
              </div>

              {/* Action */}
              <div className="col-span-1 flex justify-end">
                <button className="text-[10px] font-bold uppercase text-slate-400 hover:text-slate-800 transition-colors">Select</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!isLastPage && (
        <div className="flex justify-end mt-2">
          <Button
            onClick={() => fetchPage("more")}
            disabled={loadingMore}
            variant="secondary"
            className="px-6 py-2 h-auto text-sm font-medium flex items-center gap-2 transition-all"
          >
            {loadingMore ? <LuLoader className="animate-spin" size={16} /> : null}
            Load More
          </Button>
        </div>
      )}
      </div>
    </>
  );
};
